import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useLanguageTracker from 'hooks/useLanguageTracker';
import { FakeText, Card } from '@totalsoft/rocket-ui';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { contentTypeNameVar } from '../../variables';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { CONTENT_TYPE_QUERY } from '../../queries';
import { SAVE_CONTENT_TYPE_GROUP } from '../../mutations';
import { CONTENT_TYPE_SAVED } from '../../subscriptions';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { contentTypeValidator } from '../../validators';
import ContentTypeGeneralDataComponent from './ContentTypeGeneralDataComponent';

const ContentTypeGeneralDataContainer = ({ contentTypeId, onRedirect }) => {
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens({ isFileType: false });
  const [validation, validate] = useDirtyFieldValidation(contentTypeValidator);
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();
  const addToast = useToast();

  const { loading, refetch } = useQuery(CONTENT_TYPE_QUERY, {
    variables: { id: contentTypeId },
    skip: contentTypeId === undefined || contentTypeId === null,
    onCompleted: data => {
      resetLens(data.contentType);
    }
  });

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useEffect(() => {
    const code = get(lens.code) ? get(lens.code) : '-';
    const name = get(lens.name) ? get(lens.name) : '-';
    const concatenatedNameVar = `${name} / ${code}`;
    contentTypeNameVar(concatenatedNameVar);
  }, [lens]);

  const onLanguageChange = useCallback(() => {
    refetch();
  }, [refetch]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  useEffect(() => {
    validate(get(lens), dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  const [saveContentType] = useMutation(SAVE_CONTENT_TYPE_GROUP, {
    onError: () => {
      setSaving(false);
    }
  });

  const handleSave = useCallback(() => {
    const contentType = get(lens);
    if (!validate(contentType)) return;

    const contentTypeInput = {
      id: null,
      ...contentType
    };

    setSaving(true);
    saveContentType({ variables: { input: contentTypeInput } });
  }, [lens, saveContentType, validate]);

  useSubscription(CONTENT_TYPE_SAVED, {
    onData: ({
      data: {
        data: { contentTypeSaved }
      },
      client
    }) => {
      if (!contentTypeId) onRedirect(contentTypeSaved.contentType.id);
      resetLens(contentTypeSaved.contentType.contentType);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'contentTypeList' });
      setSaving(false);
      addToast(t('ContentTypes.ContentTypeSaved'), 'success');
    }
  });

  return (
    <>
      {loading ? (
        <Card>
          <FakeText lines={7} />
        </Card>
      ) : (
        <>
          <ContentTypeGeneralDataComponent lens={lens} isDirty={isDirty} saving={saving} onSave={handleSave} validation={validation} />
        </>
      )}
    </>
  );
};

ContentTypeGeneralDataContainer.propTypes = {
  contentTypeId: PropTypes.number,
  onRedirect: PropTypes.func
};

export default ContentTypeGeneralDataContainer;
